import React, { useMemo, useState } from 'react';
import { UserPlusIcon, Trash2Icon } from 'lucide-react';
import { Button } from '../ui/Button';
import { useRegistry, type AssignmentStatus } from '../../contexts/RegistryContext';
import { formatWhen } from '../../utils/adminFormat';
const statusLabels: Record<AssignmentStatus, string> = {
  invited: 'Invited',
  viewed: 'Viewed',
  applied: 'Applied',
  declined: 'Declined'
};
const field = 'mt-2 w-full border border-navy-100 bg-white px-3.5 py-2.5 font-sans text-[0.875rem] text-ink transition-colors duration-120 hover:border-navy-300 focus:border-navy-500';
const label = 'block font-sans text-[0.8125rem] font-medium text-ink';
export function AssignmentsPanel() {
  const {
    jobs,
    registrations,
    assignments,
    assignJob,
    setAssignmentStatus,
    removeAssignment
  } = useRegistry();
  const [jobId, setJobId] = useState('');
  const [registrationId, setRegistrationId] = useState('');
  const [note, setNote] = useState('');
  const assignable = useMemo(() => jobs.filter(j => j.status !== 'closed'), [jobs]);
  const alreadyAssigned = useMemo(() => new Set(assignments.filter(a => a.jobId === jobId).map(a => a.registrationId)), [assignments, jobId]);
  const availableCandidates = registrations.filter(r => !alreadyAssigned.has(r.id));
  const grouped = useMemo(() => jobs.map(job => ({
    job,
    rows: assignments.filter(a => a.jobId === job.id)
  })).filter(g => g.rows.length), [assignments, jobs]);
  if (!jobs.length) {
    return <p className="mt-10 border border-dashed border-navy-200 bg-white p-10 text-center font-sans text-[0.875rem] text-charcoal-500" data-id="element-43" data-mp="1kde1oycs5g9d">
        Create a job description first. Once one exists you can invite registered executives to apply to it here.
      </p>;
  }
  return <section className="mt-8" data-id="element-44" data-mp="30r49af33ymnr">
      <form className="border-l-2 border-ink bg-white p-6" onSubmit={e => {
      e.preventDefault();
      if (!jobId || !registrationId) return;
      assignJob(jobId, registrationId, note.trim());
      setRegistrationId('');
      setNote('');
    }} data-id="element-45" data-mp="30nyi8uax5ape">
        <h2 className="font-serif text-[1.25rem] text-ink" data-id="element-46" data-mp="cm8fxk1h92ef">Assign a job description</h2>
        <p className="mt-1.5 font-sans text-[0.8125rem] text-charcoal-500" data-id="element-47" data-mp="8isjanper21q">
          The executive is invited to apply — they decide whether to. An invitation is not an application.
        </p>

        <div className="mt-6 grid gap-5 lg:grid-cols-2" data-id="element-48" data-mp="108x06cdyl6y7">
          <div data-id="element-49" data-mp="w7uy7nsnqgsj">
            <label htmlFor="assign-job" className={label} data-id="element-50" data-mp="w9eg1ityozwf">
              Job description
            </label>
            <select id="assign-job" value={jobId} onChange={e => setJobId(e.target.value)} required className={field} data-id="element-51" data-mp="rftr0gacjg9d">
              <option value="" data-id="element-52" data-mp="1jz8qxuuq0ee1">Select a job…</option>
              {assignable.map(j => <option key={j.id} value={j.id} data-id="element-53" data-mp="1f5ebj3m5elkr">
                  {j.title} · {j.reference}
                </option>)}
            </select>
          </div>
          <div data-id="element-54" data-mp="1rgf1qfo0dbmr">
            <label htmlFor="assign-candidate" className={label} data-id="element-55" data-mp="1ri9qk52ehnw4">
              Registered executive
            </label>
            <select id="assign-candidate" value={registrationId} onChange={e => setRegistrationId(e.target.value)} required disabled={!registrations.length} className={field} data-id="element-56" data-mp="1wf02qhb33zc9">
              <option value="" data-id="element-57" data-mp="2af15616zzetu">
                {registrations.length ? 'Select an executive…' : 'No registrations yet'}
              </option>
              {availableCandidates.map(r => <option key={r.id} value={r.id} data-id="element-58" data-mp="2k4udmtbufdtv">
                  {r.fullName}
                  {r.professionalTitle ? ` — ${r.professionalTitle}` : ''}
                </option>)}
            </select>
          </div>
          <div className="lg:col-span-2" data-id="element-59" data-mp="2t6tv1mqrsjxc">
            <label htmlFor="assign-note" className={label} data-id="element-60" data-mp="2fjlctb81925y">
              Note to the executive
            </label>
            <textarea id="assign-note" rows={2} value={note} onChange={e => setNote(e.target.value)} placeholder="Why you think this mandate fits them." className={`${field} resize-y`} data-id="element-61" data-mp="2bj1hmd70s8mg" />
          </div>
        </div>

        <div className="mt-6 border-t border-navy-100 pt-5" data-id="element-62" data-mp="1wlqycho4xyg6">
          <Button type="submit" disabled={!jobId || !registrationId} data-id="element-63" data-mp="1sjnjrk666dqo">
            <UserPlusIcon aria-hidden="true" className="h-4 w-4" data-id="element-64" data-mp="1slgvhdvn3o5j" />
            Invite to apply
          </Button>
        </div>
      </form>

      {!grouped.length ? <p className="mt-8 border border-dashed border-navy-200 bg-white p-10 text-center font-sans text-[0.875rem] text-charcoal-500" data-id="element-65" data-mp="21japgi5xm5pj">
          Nobody has been invited yet.
        </p> : <div className="mt-10 space-y-8" data-id="element-66" data-mp="3cyftpqlpocb9">
          {grouped.map(({
        job,
        rows
      }) => <div key={job.id} data-id="element-67" data-mp="yqeul29bm6ae">
              <h3 className="font-serif text-[1.125rem] text-ink" data-id="element-68" data-mp="yq54zfs6u158">
                {job.title}{' '}
                <span className="font-sans text-[0.75rem] text-charcoal-300" data-id="element-69" data-mp="13ruf8t7l6as9">
                  {job.reference} · {rows.length} invited
                </span>
              </h3>
              <ul className="mt-3 divide-y divide-navy-100 border-y border-navy-100 bg-white" data-id="element-70" data-mp="uqnz4l8un1zj">
                {rows.map(a => <li key={a.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3.5" data-id="element-71" data-mp="1ihtcmtrbhi6o">
                    <div className="min-w-0 flex-1" data-id="element-72" data-mp="1mivdff0vu2ar">
                      <p className="font-sans text-[0.875rem] text-ink" data-id="element-73" data-mp="18ouofczqftnl">{a.candidateName}</p>
                      <p className="font-sans text-[0.8125rem] text-charcoal-500" data-id="element-74" data-mp="1dnp6gcpvle6n">{a.candidateEmail}</p>
                      {a.note ? <p className="mt-1 font-sans text-[0.8125rem] italic text-charcoal-500" data-id="element-75" data-mp="21c8f4tcu7sz9">“{a.note}”</p> : null}
                      <p className="mt-1 font-sans text-[0.6875rem] uppercase tracking-[0.1em] text-charcoal-300" data-id="element-76" data-mp="2g1jt7esedlxd">
                        {formatWhen(a.assignedAt)}
                      </p>
                    </div>
                    <select aria-label={`Status for ${a.candidateName}`} value={a.status} onChange={e => setAssignmentStatus(a.id, e.target.value as AssignmentStatus)} className="border border-navy-100 bg-white px-2.5 py-1.5 font-sans text-[0.75rem] text-ink" data-id="element-77" data-mp="261szvgfkpcyv">
                      {(Object.keys(statusLabels) as AssignmentStatus[]).map(s => <option key={s} value={s} data-id="element-78" data-mp="1id9sd34cl47u">
                          {statusLabels[s]}
                        </option>)}
                    </select>
                    <Button size="sm" variant="danger" onClick={() => removeAssignment(a.id)} data-id="element-79" data-mp="1dg9gucoqb99c">
                      <Trash2Icon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-80" data-mp="19eo9t7yvlp72" />
                      <span className="sr-only" data-id="element-81" data-mp="3kzdmvgz17pd2">Remove {a.candidateName} from {job.title}</span>
                    </Button>
                  </li>)}
              </ul>
            </div>)}
        </div>}
    </section>;
}